const EVENT_PHASE_LABELS: Record<string, string> = {
    PreMatch: 'Pre-Match',
    FirstHalf: '1st Half',
    HalfTime: 'Half-Time',
    SecondHalf: '2nd Half',
    FullTimeNormalTime: 'Full-Time',
    ExtraTimeFirstHalf: 'ET 1st Half',
    ExtraTimeHalfTime: 'ET Half-Time',
    ExtraTimeSecondHalf: 'ET 2nd Half',
    FullTimeExtraTime: 'After Extra Time',
    Penalties: 'Penalties',
    PostMatch: 'Finished',
    MatchAbandoned: 'Match Abandoned',
};

export type PhaseClockMode = 'ticking' | 'frozen' | 'hidden' | 'labelOnly';

type FootballPhaseClockConfigType = {
    mode: PhaseClockMode;
    offsetSeconds: number;
};

const MINUTES = 60;

const FOOTBALL_PHASE_CLOCK: Record<string, FootballPhaseClockConfigType> = {
    PreMatch: { mode: 'hidden', offsetSeconds: 0 },
    FirstHalf: { mode: 'ticking', offsetSeconds: 0 },
    HalfTime: { mode: 'labelOnly', offsetSeconds: 0 },
    SecondHalf: { mode: 'ticking', offsetSeconds: 45 * MINUTES },
    FullTimeNormalTime: { mode: 'frozen', offsetSeconds: 45 * MINUTES },
    ExtraTimeFirstHalf: { mode: 'ticking', offsetSeconds: 0 },
    ExtraTimeHalfTime: { mode: 'frozen', offsetSeconds: 0 },
    ExtraTimeSecondHalf: { mode: 'ticking', offsetSeconds: 45 * MINUTES },
    FullTimeExtraTime: { mode: 'frozen', offsetSeconds: 45 * MINUTES },
    Penalties: { mode: 'hidden', offsetSeconds: 0 },
    PostMatch: { mode: 'labelOnly', offsetSeconds: 0 },
    MatchAbandoned: { mode: 'labelOnly', offsetSeconds: 0 },
};

export const resolveFootballPhaseClockConfig = (
    currentPhase: string | null | undefined
): FootballPhaseClockConfigType | null => {
    if (currentPhase == null || currentPhase === '') {
        return null;
    }

    return FOOTBALL_PHASE_CLOCK[currentPhase] ?? null;
};

export const resolveFootballPhaseClockMode = (currentPhase: string | null | undefined): PhaseClockMode | null => {
    return resolveFootballPhaseClockConfig(currentPhase)?.mode ?? null;
};

export const getFootballPhaseClockOffsetSeconds = (currentPhase: string | null | undefined): number => {
    return resolveFootballPhaseClockConfig(currentPhase)?.offsetSeconds ?? 0;
};

export const formatEventPhase = (currentPhase: string | null | undefined): string | null => {
    if (currentPhase == null || currentPhase === '') {
        return null;
    }

    return EVENT_PHASE_LABELS[currentPhase] ?? currentPhase;
};

export const isEventPhaseClockTicking = (currentPhase: string | null | undefined): boolean => {
    return resolveFootballPhaseClockMode(currentPhase) === 'ticking';
};

export const parseTimeElapsedInPhase = (timeElapsedInPhase: string | null | undefined): number | null => {
    if (timeElapsedInPhase == null || timeElapsedInPhase === '') {
        return null;
    }

    const trimmed = timeElapsedInPhase.trim();
    const parts = trimmed.split(':');

    if (parts.length === 3) {
        const hours = Number(parts[0]);
        const minutes = Number(parts[1]);
        const seconds = Number(parts[2]);
        if (Number.isNaN(hours) || Number.isNaN(minutes) || Number.isNaN(seconds) || minutes > 59 || seconds > 59) {
            return null;
        }

        return hours * 3600 + minutes * 60 + seconds;
    }

    if (parts.length === 2) {
        const minutes = Number(parts[0]);
        const seconds = Number(parts[1]);
        if (Number.isNaN(minutes) || Number.isNaN(seconds) || seconds > 59) {
            return null;
        }

        return minutes * 60 + seconds;
    }

    if (/^\d+$/.test(trimmed)) {
        const totalSeconds = Number(trimmed);
        return Number.isNaN(totalSeconds) ? null : totalSeconds;
    }

    return null;
};

export const formatMinutesSeconds = (totalSeconds: number): string => {
    const safeSeconds = Math.max(0, Math.floor(totalSeconds));
    const minutes = Math.floor(safeSeconds / 60);
    const seconds = safeSeconds % 60;

    return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
};

export const toFootballMatchClockSeconds = (
    timeElapsedInPhase: string | null | undefined,
    currentPhase: string | null | undefined
): number | null => {
    const phaseElapsedSeconds = parseTimeElapsedInPhase(timeElapsedInPhase);
    if (phaseElapsedSeconds === null) {
        return null;
    }

    return phaseElapsedSeconds + getFootballPhaseClockOffsetSeconds(currentPhase);
};

export const formatFootballPhaseClockLabel = (
    mode: PhaseClockMode | null,
    displaySeconds: number | null,
    currentPhase: string | null | undefined
): string => {
    if (mode === null || mode === 'hidden') {
        return '';
    }

    if (mode === 'labelOnly') {
        return formatEventPhase(currentPhase) ?? '';
    }

    if (displaySeconds === null) {
        return '';
    }

    return [formatMinutesSeconds(displaySeconds), formatEventPhase(currentPhase)]
        .filter((part): part is string => part != null && part !== '')
        .join(' ');
};

export const formatEventPhaseLabel = (
    timeElapsedInPhase: string | null | undefined,
    currentPhase: string | null | undefined
): string => {
    return [timeElapsedInPhase, formatEventPhase(currentPhase)]
        .filter((part): part is string => part != null && part !== '')
        .join(' ');
};
